import { useMemo } from 'react'
import TabList2 from '@/components/TabList2'
import { Location } from '@/lib/definitions/api.types'
import { ProductComponentIdentifiers } from '@/lib/definitions/types'
import useExistingPorts from '@/lib/hooks/useExistingPorts'
import {
  useLoadServiceComponents,
  useSearchLocations,
} from '@/lib/queries/Services.queries'
import { processExistingPorts } from '@/pages/ServiceWizard/PortsConversion'
import { usePortSpeedOptions } from '@/pages/ServiceWizard/portSpeedOptions'
import ScrollableCountryLocationsSelector from '@/pages/ServiceWizard/partials/ScrollableCountryLocationsSelector'
import ExistingPortsDropdownSelector from '@/pages/ServiceWizard/partials/ExistingPortsDropdownSelector'

export type DynamicProductPortValue =
  | { mode: 'new'; location?: number }
  | { mode: 'existing'; port?: number }

interface DynamicProductPortFieldProps {
  value?: DynamicProductPortValue
  onChange: (value: DynamicProductPortValue) => void
  includeExtensions?: boolean
}

export const isDynamicProductPortValid = (value?: DynamicProductPortValue) =>
  value?.mode === 'new' ? !!value.location : !!value?.port

const DynamicProductPortField = ({
  value = { mode: 'new' },
  onChange,
  includeExtensions,
}: DynamicProductPortFieldProps) => {
  const portSpeedOptions = usePortSpeedOptions()
  const { data: customerExistingPorts } = useLoadServiceComponents(
    {
      pco: [ProductComponentIdentifiers.LAG, ProductComponentIdentifiers.PORT],
      excludeDependency: true,
      includeServices: true,
    },
    { enabled: value.mode === 'existing' }
  )

  const existingPorts = useMemo(
    () =>
      processExistingPorts(
        (customerExistingPorts as unknown[] | undefined) ?? [],
        portSpeedOptions
      ),
    [customerExistingPorts, portSpeedOptions]
  )

  const existingPortLocationNames = useMemo(
    () =>
      [...new Set(existingPorts.map((port) => port.locationName))]
        .sort((a, b) => a.localeCompare(b))
        .join(','),
    [existingPorts]
  )

  const { data: existingPortsLocations } = useSearchLocations(
    { name: existingPortLocationNames },
    { enabled: !!existingPortLocationNames }
  )

  const existingPortItems = useExistingPorts({
    existingPorts,
    existingPortsLocations: existingPortsLocations?.results || [],
  })

  return (
    <div className="flex flex-col gap-3">
      <TabList2
        className="my-0"
        tabs={[
          { id: 'new', label: 'New Port' },
          { id: 'existing', label: 'Existing Port' },
        ]}
        selectedTab={value.mode}
        onTabClick={(mode) =>
          onChange({ mode: mode as DynamicProductPortValue['mode'] })
        }
      />
      {value.mode === 'new' && (
        <ScrollableCountryLocationsSelector
          title=""
          selectedLocation={value.location}
          onLocationSelect={(location?: Location) =>
            onChange({ mode: 'new', location: location?.id })
          }
          includeExtensions={includeExtensions}
        />
      )}
      {value.mode === 'existing' && (
        <ExistingPortsDropdownSelector
          existingPorts={existingPortItems}
          selectedExistingPort={value.port}
          onPortSelect={(port) =>
            onChange({ mode: 'existing', port: port?.id })
          }
          isOpen={!value.port}
          title=""
        />
      )}
    </div>
  )
}

export default DynamicProductPortField
